import { CBadge } from "@coreui/react-pro";
import {
  useAssetClasses,
  useReferenceValueLabel,
  useRiskProfiles,
  useTimeframes,
} from "../../../api/hooks/useReference";

interface StrategyMetaPillsStrategy {
  assetClass: number;
  riskProfile: number;
  timeframe: number;
}

// Small pills surfacing the strategy's key metadata. Used across detail pages, browse cards,
// subscription cards, cart rows, and the dashboard cross-sell strip so metadata reads consistently.
//
// The strategy carries ids, not text — one representation from the store to here — so each pill
// resolves its label through the reference lists. Those are cached for the session, so this costs
// nothing per pill.
//
// Caller is responsible for the wrapping flex/gap container (e.g.
// `<div className="d-flex flex-wrap gap-2 mb-2">…</div>`) — that lets other pills (status, billing)
// live alongside without an extra wrapper.
export function StrategyMetaPills({
  strategy,
  withRiskProfile = false,
  withTimeframe = true,
}: {
  strategy: StrategyMetaPillsStrategy;
  /** Off beside a StrategyIdentityBand, which already shows the timeframe. */
  withTimeframe?: boolean;
  // Says the risk level in words. The identity band states it as bars, which are hover-only, so on a
  // touch device the fact a buyer most needs is the one that isn't there — which is fine on a grid
  // being scanned, and not on the screens where someone decides to pay.
  withRiskProfile?: boolean;
}) {
  const assetClassLabel = useReferenceValueLabel(useAssetClasses().data);
  const timeframeLabel = useReferenceValueLabel(useTimeframes().data);
  const riskProfileLabel = useReferenceValueLabel(useRiskProfiles().data);

  return (
    <>
      <CBadge color="secondary" shape="rounded-pill" title="Asset class">
        {assetClassLabel(strategy.assetClass)}
      </CBadge>
      {withTimeframe && (
        <CBadge color="secondary" shape="rounded-pill" title="Timeframe">
          {timeframeLabel(strategy.timeframe)}
        </CBadge>
      )}
      {withRiskProfile && (
        <CBadge
          className="strategy-risk-pill"
          shape="rounded-pill"
          title="Risk profile"
        >
          {riskProfileLabel(strategy.riskProfile)}
        </CBadge>
      )}
    </>
  );
}
